import React from 'react';
import { Button } from '../components/ui/button';
import { Shield, FileText, BarChart3, ListChecks, Map, HelpCircle, RotateCcw, Check, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Nav from '../components/Nav';
import CookieConsent from '../components/CookieConsent';
import ProductShowcase from '../components/ProductShowcase';
import Faq from '../components/Faq';
import FounderNote from '../components/FounderNote';
import CircularProgress from '../components/CircularProgress';
import Reveal from '../components/Reveal';
import { trackFunnel } from '../lib/funnel';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Nav />

      {/* Hero Section */}
      <section
        className="relative max-w-4xl lg:max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-16 sm:pt-24 sm:pb-24"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(180,180,180,0.4) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          backgroundPosition: '0 48px',
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column: copy + CTAs */}
          <div className="text-center lg:text-left">
            <div
              className="inline-flex items-center gap-2 bg-[#0a5c44]/10 border border-[#0a5c44]/20 px-4 py-2 rounded-full mb-6 sm:mb-8 opacity-0 animate-fade-up"
              style={{ animationDelay: '0s' }}
            >
              <span className="text-xs sm:text-sm font-medium text-[#0a5c44]">
                SOC2 compliance for the rest of us
              </span>
            </div>

            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-normal mb-5 sm:mb-6 leading-tight opacity-0 animate-fade-up"
              style={{ fontFamily: 'Instrument Serif, serif', animationDelay: '0s' }}
            >
              You focus on the product.<br />We'll handle the compliance.
            </h2>

            <div className="opacity-0 animate-fade-up" style={{ animationDelay: '0.15s' }}>
              <p className="text-base sm:text-lg lg:text-xl text-[#1a1a1a]/70 mb-3 max-w-3xl mx-auto lg:mx-0 leading-relaxed">
                Find out your compliance score in 5 minutes. Free. No credit card required.
              </p>

              <p className="text-sm sm:text-base text-[#1a1a1a]/60 mb-8 sm:mb-10 max-w-3xl mx-auto lg:mx-0">
                Covers SOC2, GDPR, and general data security. Everything prospects actually ask about.
              </p>
            </div>

            <div
              className="flex flex-col items-center lg:items-start gap-3 sm:gap-4 opacity-0 animate-fade-up"
              style={{ animationDelay: '0.3s' }}
            >
              <Button
                onClick={() => navigate('/wizard')}
                data-testid="hero-cta-button"
                className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors px-8 h-12 text-base w-full sm:w-auto"
              >
                See your compliance score
              </Button>
              <p className="text-sm text-[#1a1a1a]/55">Free · No credit card required</p>
              <button
                type="button"
                onClick={() => navigate('/demo')}
                data-testid="hero-sample-link"
                className="text-sm font-medium text-[#0a5c44] hover:underline inline-flex items-center gap-1"
              >
                See a sample report
                <span aria-hidden="true">→</span>
              </button>
            </div>

            {/* Mobile-only bento grid (under 768px) */}
            <div
              className="md:hidden mt-8 space-y-3 text-left"
              data-testid="hero-mobile-bento"
            >
              {[
                {
                  icon: BarChart3,
                  title: 'Compliance Score',
                  desc: 'See where you stand in 5 minutes.',
                  to: '/wizard',
                },
                {
                  icon: Shield,
                  title: 'Trust Page',
                  desc: 'A shareable page that closes deals.',
                  to: '/demo#trust-page',
                },
                {
                  icon: FileText,
                  title: 'Policy Templates',
                  desc: 'SOC2, GDPR, and data handling — ready to send.',
                  to: '/demo#policies',
                },
              ].map(({ icon: Icon, title, desc, to }) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => {
                    trackFunnel('hero_bento_clicked', {
                      properties: { card_title: title },
                    });
                    navigate(to);
                  }}
                  className="relative w-full text-left bg-[#f5f5f5] active:bg-[#ebebeb] rounded-xl p-4 pr-10 transition-colors duration-100"
                  data-testid={`hero-bento-${title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5 text-[#0a5c44] mb-2" />
                  <p className="text-base font-semibold text-[#1a1a1a] mb-0.5">
                    {title}
                  </p>
                  <p className="text-sm text-[#1a1a1a]/60 leading-snug">
                    {desc}
                  </p>
                  <ArrowRight
                    className="w-4 h-4 text-[#0a5c44] absolute bottom-3 right-3"
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>

            {/* Mobile-only product preview card (under 768px) */}
            <button
              type="button"
              data-testid="hero-mobile-preview"
              onClick={() => {
                trackFunnel('hero_preview_clicked');
                navigate('/demo#score');
              }}
              className="md:hidden mt-4 w-full text-left relative bg-[#f8faf9] active:bg-[#eef5f1] rounded-2xl p-4 pr-10 shadow-[0_2px_8px_-2px_rgba(10,92,68,0.08),0_4px_16px_-8px_rgba(10,92,68,0.06)] transition-colors duration-100"
            >
              <div className="flex items-center gap-4">
                <CircularProgress value={62} size={88} strokeWidth={8} />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-[#1a1a1a] leading-snug mb-2">
                    Solid foundation, room to grow
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-[#0a5c44] bg-[#0a5c44]/10 px-2 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0a5c44]" aria-hidden="true" />
                    Trust Page Active
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#0a5c44]/10 flex items-center justify-between gap-3">
                <span className="text-sm text-[#1a1a1a] truncate">
                  Enable MFA across accounts
                </span>
                <span className="shrink-0 inline-flex items-center text-[10px] font-bold tracking-wider uppercase text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                  High
                </span>
              </div>

              <ArrowRight
                className="w-4 h-4 text-[#0a5c44] absolute bottom-3 right-3"
                aria-hidden="true"
              />
            </button>
          </div>

          {/* Right column: dashboard mockup (desktop only) */}
          <div
            className="hidden lg:flex justify-center items-center opacity-0 animate-fade-up-scale"
            style={{ animationDelay: '0.45s' }}
            data-testid="hero-dashboard-mockup"
            aria-hidden="true"
          >
            <div
              className="relative w-full max-w-md animate-float"
              style={{
                filter: 'drop-shadow(0 25px 35px rgba(10, 92, 68, 0.18)) drop-shadow(0 10px 15px rgba(0, 0, 0, 0.08))',
              }}
            >
              <div className="bg-white border border-[#e0ddd8] rounded-2xl overflow-hidden">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-3 bg-[#f5f3ef] border-b border-[#e0ddd8]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]"></span>
                  <div className="ml-3 flex-1 text-xs text-[#1a1a1a]/50 bg-white border border-[#e0ddd8] rounded-md px-3 py-1 truncate">
                    verilock.ai/dashboard
                  </div>
                </div>

                {/* Card body */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#1a1a1a]/50 mb-1">
                        Acme Inc
                      </p>
                      <p
                        className="text-xl font-normal leading-tight"
                        style={{ fontFamily: 'Instrument Serif, serif' }}
                      >
                        Compliance overview
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0a5c44] bg-[#0a5c44]/10 px-2 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0a5c44]"></span>
                      Live
                    </span>
                  </div>

                  <div className="flex items-center gap-5 mb-6">
                    <CircularProgress value={62} size={120} strokeWidth={10} />
                    <div className="flex-1">
                      <p className="text-sm text-[#1a1a1a]/60 mb-1">Compliance score</p>
                      <p
                        className="text-2xl font-normal leading-tight mb-2"
                        style={{ fontFamily: 'Instrument Serif, serif' }}
                      >
                        62 / 100
                      </p>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0a5c44] bg-[#0a5c44]/10 px-2 py-0.5 rounded-full">
                        ↑ +8 this month
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    {[
                      { label: 'Enable 2FA across team', done: true },
                      { label: 'Publish data handling policy', done: true },
                      { label: 'Run quarterly access review', done: false },
                    ].map((task) => (
                      <div
                        key={task.label}
                        className="flex items-center gap-3 px-3 py-2 bg-[#f5f3ef] rounded-lg"
                      >
                        <span
                          className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            task.done
                              ? 'bg-[#0a5c44] text-white'
                              : 'bg-white border border-[#e0ddd8]'
                          }`}
                        >
                          {task.done && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                        </span>
                        <span
                          className={`text-xs ${
                            task.done
                              ? 'text-[#1a1a1a]/50 line-through'
                              : 'text-[#1a1a1a]'
                          }`}
                        >
                          {task.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile-only auto-scrolling ticker (under 768px) */}
      <div
        className="md:hidden bg-[#f0f7f4] overflow-hidden py-3 border-y border-[#0a5c44]/10"
        data-testid="hero-mobile-ticker"
        aria-hidden="true"
      >
        <div className="flex w-max animate-marquee">
          {Array.from({ length: 2 }).flatMap((_, dupIdx) =>
            [
              'SOC2 covered',
              'GDPR covered',
              'Under 1 hour',
              'No consultant needed',
              'Free to start',
              'Shareable Trust Page',
              'Instant compliance score',
              'No credit card required',
            ].map((item, i) => (
              <span
                key={`${dupIdx}-${i}`}
                className="inline-flex items-center text-sm font-medium text-[#0a5c44] whitespace-nowrap"
              >
                <span className="px-5">{item}</span>
                <span className="text-[#0a5c44]/40" aria-hidden="true">·</span>
              </span>
            ))
          )}
        </div>
      </div>

      {/* Product Showcase Carousel */}
      <Reveal as="div">
        <ProductShowcase />
      </Reveal>

      {/* Pain Section */}
      <Reveal
        as="section"
        className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16"
      >
        <div className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-2xl p-6 sm:p-12 text-center">
          <p
            className="text-2xl sm:text-3xl mb-5 sm:mb-6 leading-snug"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            "Are you SOC2 compliant?"<br />
            <span className="text-[#1a1a1a]/60">— Your biggest prospect, last Tuesday</span>
          </p>
          <p className="text-base sm:text-lg text-[#1a1a1a]/70 max-w-2xl mx-auto leading-relaxed">
            You Googled it. You saw $30,000–$50,000. You nearly lost the deal.<br />
            There's a better way.
          </p>
        </div>
      </Reveal>

      {/* Problem Section */}
      <Reveal
        as="section"
        className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20"
      >
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-6 sm:mb-8 leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          Compliance is broken for small teams.
        </h3>

        <div className="space-y-5 sm:space-y-6 text-base sm:text-lg text-[#1a1a1a]/70 leading-relaxed">
          <p>
            Vanta and Sprinto charge $10K–$30K per year. They're built for Series A companies with dedicated security teams, not pre-seed founders trying to close their first enterprise deal.
          </p>

          <div className="bg-[#f5f3ef] border-l-4 border-[#0a5c44] p-5 sm:p-6 rounded">
            <p
              className="text-xl sm:text-2xl font-semibold text-[#1a1a1a]"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              $30K–$50K
            </p>
            <p className="text-sm sm:text-base">average first-year SOC2 cost</p>
          </div>

          <p>
            Most early-stage founders don't need a full SOC2 audit yet. They need to show prospects they take security seriously. They need policies, documentation, and a way to communicate their security posture.
          </p>
        </div>
      </Reveal>

      {/* Solution Section */}
      <section className="bg-white py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <Reveal as="h3"
            className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-4 text-center leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            From zero to credible in under an hour.
          </Reveal>

          <Reveal
            as="div"
            stagger
            className="grid sm:grid-cols-2 gap-5 sm:gap-8 mt-10 sm:mt-16"
          >
            {[
              { number: '1', title: 'Answer 20 questions about your business', description: 'Plain English. No security jargon. Just tell us how you work.' },
              { number: '2', title: 'Get your compliance roadmap', description: 'AI-powered analysis of your current posture and what to improve.' },
              { number: '3', title: 'Receive your policy templates', description: 'Tailored to your stack, your risks, and your business model.' },
              { number: '4', title: 'Share your Trust Page', description: 'A professional page showing prospects you mean business about security.' }
            ].map((step) => (
              <div
                key={step.number}
                className="bg-white border border-[#e0ddd8] rounded-xl p-6 sm:p-8"
              >
                <div className="w-12 h-12 bg-[#0a5c44] text-white rounded-full flex items-center justify-center text-xl font-semibold mb-4">
                  {step.number}
                </div>
                <h4
                  className="text-xl sm:text-2xl mb-2 sm:mb-3 leading-snug"
                  style={{ fontFamily: 'Instrument Serif, serif' }}
                >
                  {step.title}
                </h4>
                <p className="text-sm sm:text-base text-[#1a1a1a]/70 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Pricing Section */}
      <Reveal
        as="section"
        className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20"
      >
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-4 text-center leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          One plan. One price. No surprises.
        </h3>

        <div className="max-w-md mx-auto mt-10 sm:mt-12">
          <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl p-6 sm:p-8">
            <div className="mb-6">
              <p
                className="text-4xl sm:text-5xl font-semibold mb-2"
                style={{ fontFamily: 'Instrument Serif, serif' }}
              >
                $39
              </p>
              <p className="text-[#1a1a1a]/60">per month</p>
            </div>

            <ul className="space-y-3 sm:space-y-4 mb-8">
              {[
                { icon: HelpCircle, label: '20-question compliance wizard' },
                { icon: Map, label: 'AI-powered compliance roadmap' },
                { icon: FileText, label: '3 essential policy templates (SOC2, GDPR & Data Handling)' },
                { icon: Shield, label: 'Shareable Trust Page' },
                { icon: BarChart3, label: 'Live compliance score' },
                { icon: ListChecks, label: 'Monthly security checklist' },
                { icon: RotateCcw, label: 'Cancel anytime' },
              ].map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-start gap-3 text-sm sm:text-base">
                  <Icon className="w-5 h-5 text-[#0a5c44] mt-0.5 flex-shrink-0" />
                  <span>{label}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => navigate('/wizard')}
              data-testid="pricing-cta-button"
              className="w-full bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors h-12 text-base"
            >
              Get started now
            </Button>
          </div>

          <div className="mt-8 p-5 sm:p-6 bg-[#f5f3ef] rounded-xl text-center">
            <p className="text-sm text-[#1a1a1a]/60 mb-2">Compare:</p>
            <div className="flex justify-center items-center gap-4 sm:gap-8 text-base sm:text-lg">
              <div>
                <p className="font-medium">Vanta/Sprinto</p>
                <p className="text-sm sm:text-base text-[#1a1a1a]/60">$10K–$30K/yr</p>
              </div>
              <div className="border-l border-[#e0ddd8] h-10"></div>
              <div>
                <p className="font-medium">Verilock</p>
                <p className="text-sm sm:text-base text-[#0a5c44] font-semibold">$468/yr</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Objections Section */}
      <section className="bg-[#f5f3ef] py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal as="h3"
            className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-10 sm:mb-16 text-center leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            We know what you're thinking.
          </Reveal>

          <Reveal as="div" stagger className="grid sm:grid-cols-2 gap-5 sm:gap-8">
            {[
              { question: 'Is this real compliance?', answer: "It's the compliance foundation you need to win deals, not a full SOC2 audit. When you're ready for that, we'll tell you exactly how to get there." },
              { question: 'Will prospects accept this?', answer: "Your Trust Page shows real policies, real documentation, and a real security posture. That's what most procurement teams want at this stage." },
              { question: 'What if I have no idea about security?', answer: "That's exactly who this is for. You answer plain-English questions. We handle the translation into security language." },
              { question: 'Why not just use a free template?', answer: "Free templates aren't tailored to your business, your stack, or your risk profile. Prospects can tell. Ours can't." }
            ].map((objection, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#e0ddd8] rounded-xl p-6 sm:p-8"
              >
                <h4
                  className="text-xl sm:text-2xl mb-3 sm:mb-4 leading-snug"
                  style={{ fontFamily: 'Instrument Serif, serif' }}
                >
                  {objection.question}
                </h4>
                <p className="text-sm sm:text-base text-[#1a1a1a]/70 leading-relaxed">
                  {objection.answer}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* FAQ Section */}
      <Reveal as="div">
        <Faq />
      </Reveal>

      {/* Final CTA Section */}
      <Reveal as="section" className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-5 sm:mb-6 leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          What is your compliance score?
        </h3>

        <p className="text-base sm:text-lg lg:text-xl text-[#1a1a1a]/70 mb-8 sm:mb-10 leading-relaxed">
          Find out in 5 minutes.
        </p>

        <div className="flex flex-col items-center gap-3 sm:gap-4">
          <Button
            onClick={() => navigate('/wizard')}
            data-testid="final-cta-button"
            className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors px-8 h-12 text-base w-full sm:w-auto"
          >
            See your compliance score
          </Button>
          <p className="text-sm text-[#1a1a1a]/55">Free · No credit card required</p>
        </div>
      </Reveal>

      {/* Founder note */}
      <Reveal as="div">
        <FounderNote />
      </Reveal>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Home;
